import Http from '@/services/http';
import isEmpty from 'lodash/isEmpty';
import {
  PagedRequestInstallation,
  PagedResponseInstallation,
  MySigmaAlarmRequest,
  MySigmaAlarmResponse,
  Lookup,
  GetRegionalRequest,
  AlarmRegional,
  SingleOptionsInstallationRequest,
  AlarmInstallation,
  AssetRequest,
  AssetResponse,
  SaveConfiguration,
  AlarmLookupRequest,
  AlarmLookupResponse,
} from 'sigma-mappings';

const urls = {
  view: {
    listAlarms: '/meusigma/alarme',
    getCompanies: '/meusigma/alarme/empresas',
    getRegional: '/meusigma/alarme/regionais',
    getInstallations: '/meusigma/alarme/instalacoes',
    getAssets: '/meusigma/alarme/ativos',
    lookup: '/alarme/lookup',
  },
  config: {
    getInstallations: '/meusigma/alarme/configuracao/instalacao/listagem',
    saveConfiguration: '/meusigma/alarme/configuracao',
  },
};

export default {
  /**
   *  Entrada:
   *      @param {guid} [payload.assetsIds]
   *      @param {guid} [payload.modulesIds]
   * @return Lookup de alarmes agrupados por módulo.
   * */
  lookup(payload) {
    const http = new Http();
    const mappedPayload = new AlarmLookupRequest(payload);
    return http.post(urls.view.lookup, mappedPayload)
      .then(({ data }) => data.map(item => new AlarmLookupResponse(item)));
  },

  /**
   *  @return Lookup de empresas para opções de filtro dos alarmes.
   * */
  getCompanies() {
    const http = new Http();
    return http.get(urls.view.getCompanies)
      .then(({ data }) => data.map(item => new Lookup(item)));
  },

  /**
   *  @return Lookup de regionais para opções de filtro dos alarmes.
   * */
  getRegional(filter) {
    const http = new Http();
    let params;
    if (!isEmpty(filter)) {
      params = new GetRegionalRequest({
        companyId: filter.companyId,
      });
    }
    return http.get(urls.view.getRegional, { params })
      .then(({ data }) => data.map(item => new AlarmRegional(item)));
  },

  /** Listagem de instalações de acordo com a regional ou empresa selecionadas.
   *  Entrada:
   *      @param {guid} [query.regionalId]
   *      @param {guid} [query.companyId]
   *  @return Lookup de regionais para opções de filtro dos alarmes.
   * */
  getInstallations(query) {
    let params;
    if (!isEmpty(query)) {
      params = new SingleOptionsInstallationRequest(query);
    }
    const http = new Http();
    return http.get(urls.view.getInstallations, { params })
      .then(({ data }) => data.map(item => new AlarmInstallation(item)));
  },

  /** Listagem de ativos de acordo com a regional, empresa ou instalação selecionadas.
   *  Entrada:
   *      @param {guid} [query.regionalId]
   *      @param {guid} [query.companyId]
   *      @param {guid} [query.installationId]
   *  @return Lookup de regionais para opções de filtro dos alarmes.
   * */
  getAssets(query) {
    let params;
    if (!isEmpty(query)) {
      params = new AssetRequest(query);
    }
    const http = new Http();
    return http.get(urls.view.getAssets, { params })
      .then(({ data }) => data.map(item => new AssetResponse(item)));
  },

  /** Listagem de ativos de acordo com a regional, empresa ou instalação selecionadas.
   *  Entrada:
   *      @param {guid} [payload.regionalId]
   *      @param {guid} [payload.companyId]
   *      @param {guid} [payload.installationId]
   *      @param {guid} [payload.assetId]
   *      @param {guid} [payload.alarmStatus]
   *      @param {enum} [payload.alarmClassification] - Valores possiveis:
   *        [
   *          ordinary,
   *          urgent,
   *          critical,
   *        ]
   *  @return Lookup de regionais para opções de filtro dos alarmes.
   * */
  listAlarms(payload) {
    const http = new Http();
    const mappedPayload = new MySigmaAlarmRequest(payload);
    return http.post(urls.view.listAlarms, mappedPayload)
      .then(({ data }) => data.map(item => new MySigmaAlarmResponse(item)));
  },

  /** Busca as instalações de forma paginada para configurar os alarmes exibidos.
   *  Entrada:
   *     @param {String} [payload.regionalIds]
   *     @param {Array} [payload.companiesIds]
   *     @param {Number} payload.page
   *     @param {Number} payload.pageSize
   *
   *  @return Instalações para a configuração de grupos de supervisão paginadas.
   */
  getInstallationsForConfig(payload) {
    const http = new Http();
    const mappedPayload = new PagedRequestInstallation(payload);
    return http.post(urls.config.getInstallations, mappedPayload)
      .then(response => new PagedResponseInstallation(response.data));
  },

  /** Salva a configuração de alarmes através das instalações selecionadas
   *  Entrada:
   *     @param {array} installationIds[]
   *  @return Confirmação da configuração realizada
   */
  saveConfiguration(installationIds) {
    const http = new Http();
    const configurationToSave = new SaveConfiguration({
      installations: installationIds.map(installationId => ({
        installationId,
      })),
    });
    return http.post(urls.config.saveConfiguration, configurationToSave);
  },

};
